'use client';

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type KeyboardEvent,
} from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Text } from '../../../components/styled/text';
import { Button } from '../../../components/styled/button';
import type { TerminalResult } from './hitl-types';
import {
  terminalCodeBlockStyles,
  terminalPromptColor,
  terminalTextareaStyles,
} from './terminal-theme';
import { ChatCodeBlock } from './code-block/ChatCodeBlock';

export { terminalCodeBlockStyles } from './terminal-theme';

type TerminalContentProps = {
  command: string;
  result?: TerminalResult;
  maxOutputHeight?: string;
};

/**
 * Renders terminal command and output in a consistent dark theme
 *
 * Used by:
 * - HITL approval cards (shows command pending approval)
 * - Tool result cards (shows command + output after execution)
 */
export function TerminalContent({
  command,
  result,
  maxOutputHeight = '200px',
}: TerminalContentProps) {
  return (
    <VStack gap={2} alignItems="stretch" width="100%">
      {/* Command */}
      <ChatCodeBlock code={command} prompt="$" size="sm" />
      {result?.stdout ? (
        <ChatCodeBlock code={result.stdout} size="sm" maxHeight={maxOutputHeight} wrap />
      ) : null}
      {result?.stderr ? (
        <ChatCodeBlock
          code={result.stderr}
          size="sm"
          tone="error"
          maxHeight={maxOutputHeight}
          wrap
        />
      ) : null}
    </VStack>
  );
}

type TerminalCommandDisplayProps = {
  command: string;
  isEditing?: boolean;
  onSave?: (editedCommand: string) => void;
  onCancel?: () => void;
};

function autoResizeTextarea(el: HTMLTextAreaElement) {
  el.style.height = '0';
  el.style.height = `${el.scrollHeight}px`;
}

export function TerminalCommandDisplay({
  command,
  isEditing,
  onSave,
  onCancel,
}: TerminalCommandDisplayProps) {
  const t = useTranslations('designSystem.chat.terminalCommandDisplay');
  const [editedCommand, setEditedCommand] = useState(command);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditedCommand(command);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          autoResizeTextarea(el);
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      });
    }
  }, [isEditing, command]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setEditedCommand(e.target.value);
      autoResizeTextarea(e.target);
    },
    [],
  );

  const handleSave = useCallback(() => {
    const trimmed = editedCommand.trim();
    if (trimmed && onSave) {
      onSave(trimmed);
    }
  }, [editedCommand, onSave]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSave();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
      }
    },
    [handleSave, onCancel],
  );

  if (isEditing) {
    return (
      <VStack gap={2} w="full" alignItems="stretch">
        <Box
          className={terminalCodeBlockStyles}
          borderWidth="1px"
          borderColor="blue.8"
        >
          <HStack gap={0} alignItems="flex-start" w="full">
            <Text
              as="span"
              style={{ color: terminalPromptColor }}
              flexShrink={0}
            >
              ${' '}
            </Text>
            <textarea
              ref={textareaRef}
              className={terminalTextareaStyles}
              value={editedCommand}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              rows={1}
            />
          </HStack>
        </Box>
        <HStack gap={2} justifyContent="flex-end" w="full">
          <Button variant="ghost" size="xs" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button
            variant="outline"
            size="xs"
            onClick={handleSave}
            disabled={!editedCommand.trim()}
          >
            {t('saveAndRun')}
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <Box className={terminalCodeBlockStyles} w="full">
      <Text as="span" style={{ color: terminalPromptColor }}>
        ${' '}
      </Text>
      {command}
    </Box>
  );
}
